import { FC } from 'react';
import { InfoTable } from '@/components/info-table';
import { InfoTableRowProps } from '@/components/info-table/info-table-row';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { strings } from '@/constants/strings';
import { DrawerContentLayout } from '@/layouts/drawer-content-layout';
import { AnyDevice, DeviceAssignedToOrganisation } from '@/types/device';

export type ViewDeviceProps = {
  device: AnyDevice;
  unlinkType: 'organisation' | 'unit';
  onClose: () => void;
  onDelete?: () => void;
  onUnlink: () => void;
};

const hasOrganisation = (d: AnyDevice): d is DeviceAssignedToOrganisation =>
  d.organisation !== undefined && d.organisation !== null;

export const ViewDevice: FC<ViewDeviceProps> = ({
  device,
  unlinkType,
  onClose,
  onDelete,
  onUnlink,
}) => {
  const hasUnit = 'unit' in device && device.unit !== undefined;

  const showUnlinkbutton =
    unlinkType === 'organisation' ? hasOrganisation(device) : hasUnit;

  return (
    <DrawerContentLayout
      buttonItems={[
        showUnlinkbutton && (
          <Button key="unlink" onClick={onUnlink} variant="secondary-warning">
            {unlinkType === 'organisation'
              ? strings.devices.drawer_view.unlink_organisation
              : strings.devices.drawer_view.unlink_unit}
          </Button>
        ),
        onDelete && (
          <Button key="delete" onClick={onDelete} variant="secondary-warning">
            {strings.devices.drawer_view.delete}
          </Button>
        ),
        <div className="flex-grow" key="flex"></div>,
        <Button key="close" onClick={onClose} variant="secondary">
          {strings.devices.drawer_view.close}
        </Button>,
      ]}
      mainContent={
        <InfoTable
          rows={
            [
              { label: 'Device name', value: device.friendlyName },
              {
                label: 'Device serial number (DSN)',
                value: device.serialNumber,
              },
              { label: 'Model', value: device.model },
              {
                label: 'Assigned organisation',
                value: hasOrganisation(device)
                  ? device.organisation.name
                  : 'Not Assigned',
                valueClassName: hasOrganisation(device) ? undefined : 'italic',
              },
              ...(unlinkType === 'unit'
                ? [
                    {
                      label: 'Associated unit',
                      value: hasUnit ? device.unit?.unitName : 'Non-associated',
                      valueClassName: hasUnit ? undefined : 'italic',
                    },
                  ]
                : []),
              {
                label: 'Connection status',
                value: (
                  <StatusBadge
                    text={device.connectivity?.status ?? 'Unknown'}
                    variant={
                      device.connectivity?.status === 'Online'
                        ? 'success'
                        : 'alert'
                    }
                  />
                ),
              },
              'verifiedASP' in device &&
                (device.verifiedASP === 'error' ||
                  device.verifiedASP === 'verified') && {
                  label: 'ASP Connection Verified',
                  value: (
                    <StatusBadge
                      text={
                        device.verifiedASP === 'verified' ? 'Verified' : 'Error'
                      }
                      variant={
                        device.verifiedASP === 'verified'
                          ? 'success'
                          : 'warning'
                      }
                    />
                  ),
                },
            ].filter(Boolean) as InfoTableRowProps[]
          }
          title="Device Information"
        />
      }
    />
  );
};
